import { DefaultMap, mapKeyByMany } from '@seaber/common/lib/collections';
import { findIndex, sortBy } from 'lodash';
import type { ScenarioOrder } from '../../../lib/planningData';
import { dToMsOpt, hToMs } from '../../../lib/time';
import {
  concatIterablesToSet,
  findIndexWith,
  get,
  isDefined,
  mappedFind,
  mappedMax,
  mappedMin,
} from '../../../lib/util';
import type {
  PreventFunctions,
  ScheduleData,
  VoyageSuggestion,
  VoyageSuggestionStatusMessage,
} from '../Core';
import {
  getFirstEventInLastPortCall,
  getZeroLegOrders,
  MAX_SHORTLIST_ORDERS_PER_DEPTH,
  shortlistOrdersForVoyageSuggestions,
  sortAndClipVoyageSuggestions,
  VOYAGE_SEARCH_BRANCHING_DEPTH,
} from '../Core';
import type {
  AssignableItem,
  BundledOrders,
  GetSuggestionsInputs,
  SuggestionLogTree,
} from './getSuggestions';
import {
  createBestScheduleForOrders,
  updateAssignmentsInBestSchedule,
} from './getSuggestions';
import { DeriveCapacitiesAndTimes } from '../DeriveCapacitiesAndTimes';
import {
  countTotalMassDistance,
  countVoyageCargoPerformance,
} from '../Core.voyage-metrics';
import type {
  CalculationConfig,
  ScheduleDataContext,
} from '../ScheduleDataContext';
import type { BestSchedule } from './Suggestions';
import type { TCOrderEvents, TCTimelineEvent } from '../TimeData';
import { getEventLocationId, isTCTimelineEvent } from '../TimeData';
import type { Vessel } from '../Vessel';

/** Generate a number of multi parcel voyage suggestions for the given vessel.
 * @param onStatus A callback which is called with the current progress of the computation. If it returns true, the computation is stopped.
 */
export async function getVoyageSuggestions(
  onStatus:
    | null
    | ((status: VoyageSuggestionStatusMessage) => Promise<boolean | undefined>),
  vessel: Vessel,
  context: Pick<
    ScheduleDataContext,
    | 'coasById'
    | 'distances'
    | 'portCostEstimateMaps'
    | 'locations'
    | 'portCalls'
  >,
  config: PreventFunctions<CalculationConfig>,
  availableItems: AssignableItem[],
  originalEvents: TCTimelineEvent[],
  {
    numOrders,
    objective,
    firstAllowedTime,
    lastAllowedTime,
    addToShipmentId,
    insertAfter,
    insertBefore,
    delayUntilLD,
    voyageType,
    allocationOptions,
    maxResults = 100,
    violationBasis,
    ...opts
  }: GetSuggestionsInputs,
): Promise<{
  schedules: NonNullable<BestSchedule>[];
  logs: SuggestionLogTree;
}> {
  const logs: SuggestionLogTree = {
    depth: 0,
    logEntries: [],
    branches: [],
  };

  let shouldStop = false;

  const orderEventsCache = new DefaultMap<number, Map<number, TCOrderEvents>>(
    () => new Map(),
  );
  const onlyInsertInBallast = voyageType === 'new_voyage';
  const onlyAddToExistingVoyages = voyageType === 'existing_voyage';
  const lastPCStartEvent = getFirstEventInLastPortCall(originalEvents);

  const toleratedDelayH = opts.toleratedDelayH;
  const toleratedIdleMs = hToMs(opts.toleratedIdleH);
  const violationLimitsMs = {
    load: {
      late: dToMsOpt(opts.violationLimitsD.load.late) ?? null,
      early: dToMsOpt(opts.violationLimitsD.load.early) ?? null,
    },
    discharge: {
      late: dToMsOpt(opts.violationLimitsD.discharge.late) ?? null,
      early: dToMsOpt(opts.violationLimitsD.discharge.early) ?? null,
    },
  };
  const maximumTotalDelayMs = Math.max(
    hToMs(opts.maxAllowedTotalDelayH),
    violationLimitsMs.load.late ?? 0,
    violationLimitsMs.load.early ?? 0,
    violationLimitsMs.discharge.late ?? 0,
    violationLimitsMs.discharge.early ?? 0,
  );

  const result: VoyageSuggestion[] = [];

  const eventsToScheduleData = (
    events: TCTimelineEvent[],
  ): ScheduleData<TCTimelineEvent> =>
    DeriveCapacitiesAndTimes(
      vessel,
      events,
      context,
      config,
      {
        skipSwapComputation: true,
        calculateCosts: true,
        writeTimeDataCache: true,
      },
      firstAllowedTime,
    );

  const originalDerived = eventsToScheduleData(originalEvents);
  const originalMassDistance = countTotalMassDistance(
    originalDerived.timeData,
    context.distances,
    false,
  );
  const originalNormalizedMassDistance = countTotalMassDistance(
    originalDerived.timeData,
    context.distances,
    true,
  );
  const originalDistance = originalDerived.totals.distance;

  const minimumIndex =
    insertAfter != null
      ? findIndex(originalEvents, insertAfter) + 1
      : (findIndexWith(
          originalDerived.timeData,
          0,
          1,
          (td) => td.start > firstAllowedTime,
        ) ?? originalDerived.timeData.length);

  /** Events before first allowed index, which will not be touched */
  const fixedEvents = originalEvents.slice(0, minimumIndex);

  function getMaxOrdersForDepth(depth: number): number {
    return Math.ceil(
      (get(MAX_SHORTLIST_ORDERS_PER_DEPTH, depth - 1) ??
        MAX_SHORTLIST_ORDERS_PER_DEPTH[
          MAX_SHORTLIST_ORDERS_PER_DEPTH.length - 1
        ]) * opts.computationBudget,
    );
  }

  /** Every orderId listing all the orders that it's bundled with and should be assigned together with, including itself. */
  const bundledOrders = mapKeyByMany(availableItems, (orders) =>
    orders.map((o) => o.id),
  );
  const allAvailableOrderIds = new Set(bundledOrders.keys());

  const workTotal = Math.min(availableItems.length, getMaxOrdersForDepth(1));
  let lastStatusUpdate = 0;
  let workDone = 0;

  /** Keep track of which orders we already tried to put together into a voyage. Usually trying the same orders but in a different sequence yields the same result. */
  const evaluatedOrderCombinations = new Set<string>();
  const markCombinationEvaluated = (orders: ScenarioOrder[]) => {
    const key = orders
      .map((o) => o.id)
      .sort((a, b) => a - b)
      .join('-');
    evaluatedOrderCombinations.add(key);
  };

  /** Consider the set of orders being evaluated, in any order. Complements but doesn't replace the related check for similar voyages. */
  const isCombinationEvaluated = (orders: ScenarioOrder[]): boolean => {
    const key = orders
      .map((o) => o.id)
      .sort((a, b) => a - b)
      .join('-');
    return evaluatedOrderCombinations.has(key);
  };

  function addZeroLegOrders(
    prevAddedEvents: TCTimelineEvent[],
    openOrders: Set<ScenarioOrder>,
    assignedOrders: Set<ScenarioOrder>,
    previousVCP: number,
  ):
    | undefined
    | {
        prevAddedEvents: TCTimelineEvent[];
        openOrders: Set<ScenarioOrder>;
        assignedOrders: Set<ScenarioOrder>;
        previousVCP: number;
        bestSchedule: BestSchedule;
      } {
    let addedEvents = [...prevAddedEvents];
    let vcp = previousVCP;
    let bestSchedule: BestSchedule | undefined = undefined;

    const assignedOrderIds = new Set<number>();
    for (const o of assignedOrders) {
      assignedOrderIds.add(o.id);
    }
    // Keep adding as many valid orders as we can
    while (true) {
      const prevAllEvents = [...fixedEvents, ...addedEvents];

      const previousAllDerived = eventsToScheduleData(prevAllEvents);
      vcp = countVoyageCargoPerformance(
        previousAllDerived,
        originalMassDistance,
        originalDistance,
        context.distances,
      );

      const zeroLegOrders = getZeroLegOrders(
        openOrders.values(),
        previousAllDerived.timeData,
        minimumIndex,
        config.locationsById,
        { orderEventsCache },
      );

      // Use the first order having a valid scheduling
      const [bestScheduleForOrder, orders] = mappedFind(
        zeroLegOrders,
        (orderPlacement) => {
          const orders = bundledOrders
            .get(orderPlacement.order.id)
            ?.filter((order) => !assignedOrders.has(order)) ?? [
            orderPlacement.order,
          ];
          if (isCombinationEvaluated([...assignedOrders, ...orders]))
            return null;
          const bestSchedule = createBestScheduleForOrders(
            orders,
            vessel,
            prevAllEvents,
            originalDerived,
            context,
            config,
            {
              delayUntilLD,
              firstAllowedTime,
              maximumTotalDelayMs,
              violationLimitsMs,
              maxBallastDistanceNm: opts.maxBallastDistanceNm,
              minimumIndex,
              insertBefore,
              objective,
              onlyInsertInBallast,
              assignedOrderIds,
              onlyInsertInExistingPortCalls: true,
              preComputedOriginalAllDerived: previousAllDerived,
              allocationOptions,
              orderEventsCache,
              respectCargoExclusions: opts.cargoExclusions === 'exclude',
              allAvailableOrderIds,
              violationBasis,
              shipmentId: addToShipmentId ?? null,
            },
          );

          if (!bestSchedule) return null;
          markCombinationEvaluated([...assignedOrders, ...orders]);
          for (const order of orders) {
            assignedOrderIds.add(order.id);
          }

          bestSchedule.combination.assignments =
            updateAssignmentsInBestSchedule(
              [...assignedOrders, ...orders],
              bestSchedule,
            );
          return [bestSchedule, orders];
        },
      ) ?? [null, null];

      // If no order could have a valid placement we're done
      if (!bestScheduleForOrder || !orders) {
        break;
      }

      addedEvents =
        bestScheduleForOrder.combination.schedule.slice(minimumIndex);

      for (const order of orders ?? []) {
        openOrders.delete(order);
        assignedOrders.add(order);
      }
      bestSchedule = bestScheduleForOrder;
    }

    return (
      bestSchedule && {
        prevAddedEvents: addedEvents,
        openOrders,
        assignedOrders,
        previousVCP: vcp,
        bestSchedule,
      }
    );
  }

  /** Returns if the suggestion was added to the result: if not, the same or similar suggestion already exists */
  function cleanAndPushResult(suggestion: VoyageSuggestion): boolean {
    if (result.find((s) => isSimilarVoyageSuggestion(s, suggestion)))
      return false;
    result.push(suggestion);
    return true;
  }

  /** Groups each order with orders its supposed to be assigned together with, and removes duplicate bundles. */
  function ordersToOrderGroups(
    orders: ScenarioOrder[],
    bundledOrders: Map<number, ScenarioOrder[]>,
  ): AssignableItem[] {
    const result: AssignableItem[] = [];
    const alreadyAddedIds = new Set<number>();
    for (const order of orders) {
      if (alreadyAddedIds.has(order.id)) continue;
      const bundle = bundledOrders.get(order.id) ?? [order];
      result.push(bundle);
      for (const order of bundle) {
        alreadyAddedIds.add(order.id);
      }
    }
    return result;
  }

  async function addOrdersToVoyage(
    /** events start from first allowed index before calling this recursion of the function */
    prevAddedEvents: TCTimelineEvent[],
    /** all possible orders not yet assigned **/
    openOrders: Set<ScenarioOrder>,
    assignedOrders: Set<ScenarioOrder>,
    previousVCP: number,
    logs: SuggestionLogTree,
    /** Starts at 1 */
    depth = 1,
  ) {
    if (openOrders.size === 0 || shouldStop) {
      return;
    }

    // Status update every x * 1000ms
    if (Date.now() > lastStatusUpdate + 1000) {
      sortAndClipVoyageSuggestions(
        result,
        maxResults,
        toleratedDelayH,
        toleratedIdleMs,
      );
      shouldStop = !!(await onStatus?.({
        type: 'progress',
        workDone,
        workTotal,
        isStopped: false,
        currentResults: getResult(),
      }));
      lastStatusUpdate = Date.now();
    }

    // Housekeeping, when we have very many combinations to try we'll periodically drop most irrelevant (bad) results to save memory
    if (result.length > maxResults * 5) {
      sortAndClipVoyageSuggestions(
        result,
        maxResults,
        toleratedDelayH,
        toleratedIdleMs,
      );
    }

    if (depth > 1) {
      /** There may be especially good orders available, add these freely without any branching or other considerations */
      const zeroLegAdditionsResult = addZeroLegOrders(
        prevAddedEvents,
        openOrders,
        assignedOrders,
        previousVCP,
      );

      if (zeroLegAdditionsResult) {
        prevAddedEvents = zeroLegAdditionsResult.prevAddedEvents;
        openOrders = zeroLegAdditionsResult.openOrders;
        assignedOrders = zeroLegAdditionsResult.assignedOrders;
        previousVCP = zeroLegAdditionsResult.previousVCP;

        cleanAndPushResult({
          voyageCargoPerformance: zeroLegAdditionsResult.previousVCP,
          schedule: zeroLegAdditionsResult.bestSchedule,
        });
      }
    }

    if (depth > numOrders || shouldStop) {
      return;
    }

    /** Max number of orders to branch on for the next depth level */
    const maxNextOrders = getMaxOrdersForDepth(depth);

    const prevAllEvents = [...fixedEvents, ...prevAddedEvents];

    const previousAllDerived = eventsToScheduleData(prevAllEvents);

    const continueOnlyBest = depth > VOYAGE_SEARCH_BRANCHING_DEPTH;

    const potentialOrders = shortlistOrdersForVoyageSuggestions(
      openOrders,
      previousAllDerived.timeData,
      minimumIndex,
      assignedOrders,
      isCombinationEvaluated,
      vessel,
      context.distances,
      // We shortlist and evaluate 10x more orders than we branch on
      maxNextOrders * 10,
      { logEntries: logs.logEntries },
      { maxIdleMs: toleratedIdleMs },
    );

    // Bundle orders on the same voyage charters and assign together
    const potentialItems = ordersToOrderGroups(potentialOrders, bundledOrders);

    const solutions: (null | {
      orders: BundledOrders;
      vcp: number;
      /** Normalized vpc (mass counted as = 1) */
      nvcp: number;
      bestSchedule: NonNullable<BestSchedule>;
      newAssignedOrders: Set<ScenarioOrder>;
    })[] = potentialItems.map((orders) => {
      if (isCombinationEvaluated([...assignedOrders, ...orders])) return null;
      markCombinationEvaluated([...assignedOrders, ...orders]);

      const assignedOrderIds = new Set<number>();
      for (const o of assignedOrders) {
        assignedOrderIds.add(o.id);
      }
      const bestScheduleForOrder = createBestScheduleForOrders(
        orders,
        vessel,
        prevAllEvents,
        originalDerived,
        context,
        config,
        {
          delayUntilLD,
          firstAllowedTime,
          maximumTotalDelayMs,
          violationLimitsMs,
          maxBallastDistanceNm: opts.maxBallastDistanceNm,
          minimumIndex,
          insertBefore:
            insertBefore ??
            (onlyAddToExistingVoyages && depth === 1 ? lastPCStartEvent : null),
          objective,
          onlyInsertInBallast,
          assignedOrderIds: assignedOrderIds,
          preComputedOriginalAllDerived: previousAllDerived,
          allocationOptions,
          orderEventsCache,
          respectCargoExclusions: opts.cargoExclusions === 'exclude',
          allAvailableOrderIds,
          violationBasis,
          shipmentId: addToShipmentId ?? null,
          logEntries: logs.logEntries,
        },
      );

      if (!bestScheduleForOrder) {
        logs.logEntries.push({
          stage: 'combinations',
          orderIds: orders.map((o) => o.id),
          reason: 'no_valid_schedule',
        });

        return null;
      }

      const newAssignedOrders = concatIterablesToSet(assignedOrders, orders);
      bestScheduleForOrder.combination.assignments =
        updateAssignmentsInBestSchedule(
          [...newAssignedOrders],
          bestScheduleForOrder,
        );

      const voyageCargoPerformance = countVoyageCargoPerformance(
        bestScheduleForOrder,
        originalMassDistance,
        originalDistance,
        context.distances,
      );
      const normalizedVoyageCargoPerformance = countVoyageCargoPerformance(
        bestScheduleForOrder,
        originalNormalizedMassDistance,
        originalDistance,
        context.distances,
        true,
      );

      const wasAdded = cleanAndPushResult({
        voyageCargoPerformance,
        schedule: bestScheduleForOrder,
      });

      if (!wasAdded) {
        logs.logEntries.push({
          stage: 'combinations',
          reason: 'identical_voyage',
          orderIds: orders.map((o) => o.id),
        });
        return null;
      }

      logs.logEntries.push({
        stage: 'combinations',
        reason: 'is_valid',
        orderIds: orders.map((o) => o.id),
      });

      return {
        orders,
        vcp: voyageCargoPerformance,
        nvcp: normalizedVoyageCargoPerformance,
        bestSchedule: bestScheduleForOrder,
        newAssignedOrders,
      };
    });

    const sortedSolutions = sortBy(
      solutions.filter(isDefined),
      ({ vcp, nvcp }) => (continueOnlyBest ? -vcp : -nvcp),
    );

    const branchedSolutions = sortedSolutions.slice(
      0,
      continueOnlyBest ? 1 : maxNextOrders,
    );
    const remainingSolutions = sortedSolutions.slice(
      continueOnlyBest ? 1 : maxNextOrders,
    );

    logs.logEntries.push({
      stage: 'solutions',
      orderIds: branchedSolutions.flatMap((s) => s.orders.map((o) => o.id)),
      reason: 'low_solution_vcp',
    });
    logs.logEntries.push({
      stage: 'solutions',
      orderIds: remainingSolutions.flatMap((s) => s.orders.map((o) => o.id)),
      reason: 'selected',
    });

    for (const {
      orders,
      vcp,
      bestSchedule,
      newAssignedOrders,
    } of branchedSolutions) {
      if (continueOnlyBest && vcp <= previousVCP) break;
      if (depth === 1) {
        workDone++;
      }

      const currentEvents = bestSchedule.timeData
        .slice(minimumIndex)
        .map((td) => td.event)
        .filter(isTCTimelineEvent);

      const nextLogs: SuggestionLogTree = {
        logEntries: [],
        branches: [],
        depth: depth + 1,
      };
      logs.branches.push(nextLogs);

      const newOpenOrders = new Set(openOrders);
      for (const order of orders) {
        newOpenOrders.delete(order);
      }
      await addOrdersToVoyage(
        currentEvents,
        newOpenOrders,
        newAssignedOrders,
        vcp,
        nextLogs,
        depth + 1,
      );
    }
  }

  await addOrdersToVoyage(
    originalEvents.slice(minimumIndex),
    new Set(
      availableItems
        .flat()
        .sort(
          (a, b) => (a.loadingWindow.start ?? 0) - (b.loadingWindow.start ?? 0),
        ),
    ),
    new Set(),
    0,
    logs,
    1,
  );

  function getResult(): NonNullable<BestSchedule>[] {
    sortAndClipVoyageSuggestions(
      result,
      maxResults,
      toleratedDelayH,
      toleratedIdleMs,
    );
    return result.map(({ schedule }) => schedule);
  }

  const finalResult = getResult();
  onStatus?.({
    type: 'complete',
    workDone,
    workTotal,
    isStopped: shouldStop,
    currentResults: finalResult,
  });
  return { schedules: finalResult, logs: logs };
}

/** Used to filter out results which yielded practically identical results considering VCP and locations visited.
 * This both improves performance and reduces the number of similar results shown to the user.
 * Note that this method complements the other check, isCombinationEvaluted, with a different approach. */
function isSimilarVoyageSuggestion(
  a: VoyageSuggestion,
  b: VoyageSuggestion,
): boolean {
  if (a.voyageCargoPerformance !== b.voyageCargoPerformance) return false;

  const { assignments } = a.schedule.combination;
  const minIndex = mappedMin(assignments, (a) => a.loadIndex);
  const maxIndex = mappedMax(assignments, (a) => a.dischargeIndex);

  // Check every event the new events range for the same location. Starting from the end might often find different events faster.
  for (let i = maxIndex; i >= minIndex; --i) {
    const aEvent = a.schedule.combination.schedule.at(i);
    const bEvent = b.schedule.combination.schedule.at(i);

    if (
      (aEvent && getEventLocationId(aEvent)) !==
      (bEvent && getEventLocationId(bEvent))
    ) {
      return false;
    }
  }
  return true;
}
